/**
 * Модуль для работы с достижениями
 */

import { logger } from "../../utils/logger.js";

const ACHIEVEMENTS_CACHE_TTL_MS = 60000; // 60 секунд

/**
 * Создаёт менеджер для работы с достижениями
 */
export function createAchievementsManager({
  safeFetch,
  buildAuthHeaders,
  getApiUrl,
  getTelegramWebApp,
  showScreen,
  escapeHtmlFn,
  documentRef = document,
}) {
  let cachedAchievements = null;
  let cacheTimestamp = 0;
  let currentFilter = "all";

  /**
   * Загружает список достижений с кэшированием
   */
  async function fetchAchievements(force = false) {
    const timestamp = Date.now();
    if (!force && cachedAchievements && timestamp - cacheTimestamp < ACHIEVEMENTS_CACHE_TTL_MS) {
      return cachedAchievements;
    }

    try {
      const apiUrl = getApiUrl();
      const response = await safeFetch(`${apiUrl}/achievements`, {
        headers: buildAuthHeaders(),
      });

      if (!response.success) {
        throw new Error(response.error || "Ошибка загрузки достижений");
      }

      if (!response.data || !response.data.success || !Array.isArray(response.data.achievements)) {
        throw new Error("Некорректный ответ сервера (achievements)");
      }

      cachedAchievements = response.data.achievements;
      cacheTimestamp = timestamp;
      return cachedAchievements;
    } catch (error) {
      logger.error("Fetch achievements error:", error);
      throw error;
    }
  }

  /**
   * Загружает и отображает достижения с фильтрацией
   */
  async function loadAchievements(filter = "all") {
    currentFilter = filter;

    // Обновляем активный фильтр
    documentRef
      .querySelectorAll('[id^="achievements-filter-"]')
      .forEach((el) => {
        el.classList.remove("active");
        el.style.background = "";
        el.style.color = "";
      });

    const domCache = window.__domCacheManager;
    const loadingManager = window.__loadingIndicatorManager;
    
    const filterButton = domCache?.getCachedElement(`#achievements-filter-${filter}`, documentRef) 
      || documentRef.getElementById(`achievements-filter-${filter}`);
    if (filterButton) {
      filterButton.classList.add("active");
      filterButton.style.background = "var(--tg-theme-button-color)";
      filterButton.style.color = "white";
    }
    
    const container = domCache?.getCachedElement("#achievements-list", documentRef) 
      || documentRef.getElementById("achievements-list");
    
    if (!container) {
      logger.error("Элемент achievements-list не найден");
      return;
    }

    // Показываем skeleton loader
    if (loadingManager) {
      loadingManager.showLoading("achievements-load", {
        target: container,
        type: "skeleton"
      });
    } else {
      const loadingHTML = '<div class="loading">Загрузка...</div>';
      domCache?.updateHTML("#achievements-list", loadingHTML, documentRef) ||
      (container.innerHTML = loadingHTML);
    }

    try {
      const achievements = await fetchAchievements();
      if (!achievements) {
        const telegramWebApp = getTelegramWebApp();
        if (telegramWebApp?.showAlert) {
          telegramWebApp.showAlert("Ошибка загрузки достижений");
        }
        const errorHTML = '<div class="error">Ошибка загрузки достижений</div>';
        domCache?.updateHTML("#achievements-list", errorHTML, documentRef) ||
        (container.innerHTML = errorHTML);
        return;
      }

      container.innerHTML = "";

      let filteredAchievements = achievements || [];
      const total = filteredAchievements.length;
      const unlocked = filteredAchievements.filter((a) => a.unlocked).length;

      // Обновляем прогресс через domCache
      const progressText = `${unlocked} / ${total}`;
      domCache?.updateText("#achievements-progress", progressText, documentRef) ||
      (() => {
        const el = documentRef.getElementById("achievements-progress");
        if (el) el.textContent = progressText;
      })();

      domCache?.updateText("#home-achievements-progress", progressText, documentRef) ||
      (() => {
        const el = documentRef.getElementById("home-achievements-progress");
        if (el) el.textContent = progressText;
      })();

      // Фильтруем по категории
      if (filter !== "all") {
        filteredAchievements = filteredAchievements.filter(
          (a) => a.category === filter,
        );
      }

      if (filteredAchievements.length === 0) {
        const emptyHTML = '<div class="loading">Нет достижений в этой категории</div>';
        domCache?.updateHTML("#achievements-list", emptyHTML, documentRef) ||
        (container.innerHTML = emptyHTML);
        return;
      }

      // Рендерим достижения
      filteredAchievements.forEach((achievement) => {
        const item = documentRef.createElement("div");
        item.className = "gift-item";
        item.style.marginBottom = "12px";
        item.style.position = "relative";
        item.style.padding = "16px";
        item.style.borderRadius = "12px";
        item.style.background =
          "var(--tg-theme-secondary-bg-color, #f1f1f1)";
        item.style.opacity = achievement.unlocked ? "1" : "0.7";
        item.style.border = achievement.unlocked
          ? "2px solid var(--tg-theme-link-color, #3390ec)"
          : "2px solid transparent";

        const rewardValue = achievement.reward_value || {};
        const rewardText =
          achievement.reward_type === "coins"
            ? `💰 ${rewardValue.coins || 0} монет`
            : achievement.reward_type === "stars"
              ? `⭐ ${rewardValue.stars || 0} звёзд`
              : "";

        const requirementValue = achievement.requirement_value || {};
        let progressHtml = "";

        if (achievement.unlocked) {
          const unlockedDate = achievement.unlocked_at
            ? new Date(achievement.unlocked_at).toLocaleDateString("ru-RU")
            : "";
          progressHtml = `
            <div style="font-size: 12px; color: var(--tg-theme-link-color, #3390ec); margin-top: 8px; font-weight: 600;">
              ✅ Разблокировано ${unlockedDate}
            </div>
          `;
        } else {
          let current = 0;
          let required = 0;
          let progressText = "";

          if (achievement.requirement_type === "count") {
            required = requirementValue.count || 0;
            if (
              achievement.progress &&
              typeof achievement.progress === "object"
            ) {
              current = achievement.progress.count || 0;
            }
            progressText = `${current} / ${required}`;
          } else if (achievement.requirement_type === "level") {
            required = requirementValue.level || 0;
            if (
              achievement.progress &&
              typeof achievement.progress === "object"
            ) {
              current = achievement.progress.level || 0;
            }
            progressText = `Уровень ${current} / ${required}`;
          } else if (achievement.requirement_type === "value") {
            required = requirementValue.value || 0;
            if (
              achievement.progress &&
              typeof achievement.progress === "object"
            ) {
              current = achievement.progress.value || 0;
            }
            progressText = `${current} / ${required}`;
          }

          const progressPercent = required > 0 ? Math.min(100, (current / required) * 100) : 0;

          if (required > 0) {
            progressHtml = `
              <div style="font-size: 12px; color: var(--tg-theme-hint-color, #999999); margin-top: 8px;">
                Прогресс: ${progressText}
              </div>
              <div class="progress-bar" style="margin-top: 6px; height: 6px;">
                <div class="progress-fill" style="width: ${progressPercent}%; background: var(--tg-theme-link-color, #3390ec);"></div>
              </div>
            `;
          } else {
            progressHtml = `
              <div style="font-size: 12px; color: var(--tg-theme-hint-color, #999999); margin-top: 8px;">
                🔒 Заблокировано
              </div>
            `;
          }
        }

        const name = escapeHtmlFn(achievement.name || "");
        const description = escapeHtmlFn(achievement.description || "");
        const icon = achievement.icon || "🏆";

        item.innerHTML = `
          <div style="display: flex; align-items: flex-start; gap: 12px;">
            <div style="font-size: 32px; flex-shrink: 0;">${icon}</div>
            <div style="flex: 1; min-width: 0;">
              <div style="font-weight: 600; font-size: 16px; margin-bottom: 4px;">${name}</div>
              <div style="font-size: 13px; color: var(--tg-theme-hint-color, #999999); line-height: 1.4; margin-bottom: 8px;">
                ${description}
              </div>
              ${progressHtml}
              ${
                rewardText
                  ? `
                <div style="font-size: 13px; color: var(--tg-theme-link-color, #3390ec); margin-top: 8px; font-weight: 600;">
                  Награда: ${rewardText}
                </div>
              `
                  : ""
              }
            </div>
          </div>
          <div style="position: absolute; top: 12px; right: 12px; font-size: 24px;">
            ${achievement.unlocked ? "✅" : "🔒"}
          </div>
        `;

        container.appendChild(item);
      });
    } catch (error) {
      logger.error("Load achievements error:", error);
      const errorHTML = '<div class="error">Ошибка загрузки достижений</div>';
      domCache?.updateHTML("#achievements-list", errorHTML, documentRef) ||
      (() => {
        const container = documentRef.getElementById("achievements-list");
        if (container) container.innerHTML = errorHTML;
      })();
      const telegramWebApp = getTelegramWebApp();
      if (telegramWebApp?.showAlert) {
        telegramWebApp.showAlert(
          "Ошибка загрузки достижений: " +
            (error?.message || "Неизвестная ошибка"),
        );
      }
    }
  }

  /**
   * Показывает экран достижений с фильтром
   */
  function showAchievements(filter = "all") {
    currentFilter = filter;
    showScreen("achievements");
    // Загружаем достижения с указанным фильтром
    loadAchievements(filter);
  }

  /**
   * Загружает статистику достижений для главного экрана
   */
  async function loadAchievementsStats() {
    try {
      const achievements = await fetchAchievements();
      if (achievements) {
        const total = achievements.length;
        const unlocked = achievements.filter((a) => a.unlocked).length;
        const progressEl = documentRef.getElementById("home-achievements-progress");
        if (progressEl) {
          progressEl.textContent = `${unlocked} / ${total}`;
        }
      }
    } catch (error) {
      console.error("Load achievements stats error:", error);
    }
  }

  /**
   * Получает текущий активный фильтр
   */
  function getCurrentFilter() {
    return currentFilter;
  }

  return {
    fetchAchievements,
    loadAchievements,
    showAchievements,
    loadAchievementsStats,
    getCurrentFilter,
  };
}

